import React, { useEffect, useMemo } from 'react';
import ReactTooltip from 'react-tooltip';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';

import Match from './Match';
import { hasChildDraws } from '../../../utils/courtSchedular';
import Img from '../../../components/Img';
import { sportTypeIds } from '../../../utils/eventConstants';
import { getUnscheduledMatchesOfDraw } from '../../../actions/court-scheduler-actions';

import styles from './courtSchedular.scss';

const DrawListItem = (props) => {
    const {
        selectedDraw,
        draw,
        parentDraw = null,
        setSelectedDraw,
        event,
        className,
        onDrawSettingEdit,
        sportTypeId,
    } = props;

    const { childDraws, customDrawId } = draw;
    const courtScheduler = useSelector(({ courtScheduler }) => courtScheduler);
    const dispatch = useDispatch();

    const isDrawOpen = useMemo(() => {
        if (customDrawId !== selectedDraw?.customDrawId && childDraws?.length > 0) {
            return draw?.childDraws.reduce((isOpen, cd) => {
                if (!isOpen) {
                    return cd.customDrawId === selectedDraw?.customDrawId;
                }
                return isOpen;
            }, false);
        }
        return customDrawId === selectedDraw?.customDrawId;
    }, [selectedDraw, draw]);

    const hasChildren = hasChildDraws(draw);

    const isAllScheduled = draw.totalMatchCount !== null &&
        draw.totalMatchCount > 0 &&
        draw.scheduledMatchCount === draw.totalMatchCount;

    const unselectableDraw = !hasChildren && isAllScheduled;

    useEffect(() => {
        if (isDrawOpen && draw.id !== courtScheduler.drawIdToScheduleMatches && (!draw.childDraws || draw.childDraws?.length <= 0)) {
            dispatch(getUnscheduledMatchesOfDraw(event.id, draw.id, parentDraw, draw.drawColor, sportTypeId));
        }
    }, [isDrawOpen, event]);

    const onClickItem = () => {
        if (unselectableDraw) {
            return;
        }

        if (selectedDraw?.customDrawId === customDrawId) {
            if (parentDraw) {
                setSelectedDraw(parentDraw);
            } else {
                setSelectedDraw(null);
            }
        } else {
            setSelectedDraw(draw);
        }
    };

    const getTooltipText = () => {
        if (draw.totalMatchCount === null || hasChildren) {
            return undefined;
        }
        if (isAllScheduled) {
            return 'Scheduling Complete';
        }
        return `${draw.scheduledMatchCount} of ${draw.totalMatchCount} Matches Scheduled`;
    };

    const onClickSettings = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        onDrawSettingEdit(draw);
    };

    return (
        <div className={`${styles.drawListItem} ${className || ''}`}>
            <div
                className={`d-flex align-items-center ${styles.parentSection}`}
                onClick={onClickItem}
                data-tip={getTooltipText()}
                data-testid={`court-scheduler.draw-${draw.id}`}
            >
                {unselectableDraw && (
                    <div className={styles.arrow}>
                        <i className={`fas fa-check ${styles.checkmark}`} />
                    </div>
                )}
                {!unselectableDraw && (
                    <div className={styles.arrow}>
                        {isDrawOpen && (
                            <i className={`fas fa-solid fa-caret-down`} />
                        )}
                        {!isDrawOpen && (
                            <i className={`fas fa-solid fa-caret-right`} />
                        )}
                    </div>
                )}
                <div className="flex-1 mw0 ml4 mr4 fw600" data-testid={`court-scheduler.draw-${draw.id}.name`}>
                    {draw.name}
                    {draw.totalMatchCount !== null && !hasChildren && (
                        <span className={styles.matchCount} data-testid={`court-scheduler.draw-${draw.id}.match-count`}>
                            {` (${draw.scheduledMatchCount}/${draw.totalMatchCount})`}
                        </span>
                    )}
                </div>
                {draw.canEditDraw && (
                    <button
                        className={`btn ${styles.drawSettings} p0 mr4`}
                        type="button"
                        onClick={onClickSettings}
                        data-testid={`court-scheduler.draw-${draw.id}.settings-btn`}
                    >
                        <Img src="/img/v3/icons/icon-24px-settings-blue.svg" />
                    </button>
                )}
                {draw?.drawColor && (
                    <div
                        className={`${styles.sample}`}
                        style={{ backgroundColor: draw.drawColor }}
                    />
                )}
            </div>
            {getTooltipText() && <ReactTooltip place="bottom" type="dark" effect="solid" />}
            {isDrawOpen && (
                <div className={`d-flex align-items-start ${styles.childSection}`}>
                    <div className={`${styles.arrow}`} />
                    <div className="flex-1 mw0 ml4">
                        {hasChildren && draw.childDraws.map((childDraw) => {
                            return (
                                <DrawListItem
                                    key={`child-draw-${childDraw.id}`}
                                    draw={childDraw}
                                    parentDraw={draw}
                                    selectedDraw={selectedDraw}
                                    setSelectedDraw={setSelectedDraw}
                                    event={event}
                                    className={styles.childDraw}
                                    onDrawSettingEdit={onDrawSettingEdit}
                                    sportTypeId={sportTypeId}
                                />
                            );
                        })}
                        {!hasChildren && (draw.id === courtScheduler.drawIdToScheduleMatches) && (
                            <div className={styles.drawMatches}>
                                {courtScheduler.unScheduledMatches?.map((drawMatch) => {
                                    return (
                                        <Match
                                            key={`match-${drawMatch.id}`}
                                            match={drawMatch}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

DrawListItem.propTypes = {
    selectedDraw: PropTypes.objectOf(any),
    draw: PropTypes.objectOf(any),
    parentDraw: PropTypes.objectOf(any),
    setSelectedDraw: PropTypes.func.isRequired,
    event: PropTypes.objectOf(any).isRequired,
    className: PropTypes.string,
    onDrawSettingEdit: PropTypes.func.isRequired,
    sportTypeId: PropTypes.number,
};

DrawListItem.defaultProps = {
    selectedDraw: null,
    draw: {},
    parentDraw: null,
    className: null,
    sportTypeId: sportTypeIds.TENNIS,
};

export default DrawListItem;
